import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { useIntegrationsOverviewQuery } from "./IntegrationsOverview.generated.js";
import { getIntegrationMeta } from "./integrationRegistry.js";
import { INTEGRATIONS_BASE_PATH } from "./paths.js";
import { ConnectionIdsCard } from "./components/ConnectionIdsCard.js";
import {
  IntegrationCard,
  type IntegrationCardData,
} from "./components/IntegrationCard.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

export const IntegrationsOverview = ({ submenuItems }: Props) => {
  const { t } = useTranslate(["Integrations", "OrganisationPageTopbar"]);
  const client = useApolloClient();

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Organisation", { ns: "OrganisationPageTopbar" }),
      to: "/organisation",
    },
    { title: t("Integrations"), to: INTEGRATIONS_BASE_PATH },
  ];

  const query = useIntegrationsOverviewQuery({
    client,
    fetchPolicy: "cache-and-network",
  });

  const allIntegrations =
    getDataOrNull(query.data?.integrations)?.edges.map(e => e.node) ?? [];
  const orgIntegrations =
    getDataOrNull(query.data?.organisationIntegrations)?.edges.map(
      e => e.node
    ) ?? [];

  const orgIntegrationMap = React.useMemo(() => {
    const map = new Map<string, (typeof orgIntegrations)[number]>();
    for (const oi of orgIntegrations) {
      map.set(oi.integration.integrationId, oi);
    }
    return map;
  }, [orgIntegrations]);

  const cards: IntegrationCardData[] = React.useMemo(() => {
    return allIntegrations
      .filter(
        integration => !getIntegrationMeta(integration.integrationId).hidden
      )
      .map(integration => {
        const orgIntegration = orgIntegrationMap.get(integration.integrationId);
        const meta = getIntegrationMeta(integration.integrationId);
        return {
          integrationId: integration.integrationId,
          name: meta.sidebarLabel ?? integration.name,
          isConnected: !!orgIntegration,
          isForeignAccountIdRequired: integration.isForeignAccountIdRequired,
          health: orgIntegration?.health ?? null,
          meta,
        };
      });
  }, [allIntegrations, orgIntegrationMap]);

  const nothingConnected =
    !query.loading && cards.every(card => !card.isConnected);

  return (
    <Page
      title={t("Integrations")}
      subtitle={t("Connect and manage your external systems")}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
      breadcrumbsSize="medium"
    >
      <PageContainer>
        {nothingConnected && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("Connect MeisterSystems with your other programs.")}
          </Typography>
        )}
        <Stack spacing={1}>
          {cards.map(card => (
            <IntegrationCard key={card.integrationId} data={card} />
          ))}
        </Stack>

        {/* Org-level IDs external admins need during setup — preserved
            from the former "Data & Integrations" box */}
        <Stack sx={{ mt: 2 }}>
          <ConnectionIdsCard />
        </Stack>
      </PageContainer>
    </Page>
  );
};
